import React, { useEffect, useState } from 'react'

import { makeStyles } from '@material-ui/core/styles'
import { Container, Grid, Paper } from '@material-ui/core'
import { t } from 'i18n'
import Logo from 'images/fast-law-logo-color.svg'
import Loading from 'components/loading'
import { commonStyles, fetchJson, SignUp, SignupContext } from './common'
import { CredentialsFormDescription, ConfirmationFormDescription, CompanyFormDescription } from './descriptions'
import { CredentialsForm } from './credentialsForm'
import { ConfirmationForm } from './confirmationForm'
import { CompanyForm } from './companyForm'
import { PlanForm } from './planForm'
import { DatasetForm } from './datasetForm'
import CompletedForm from './completedForm'
import LanguageSelect from './languageSelect'
import I18n from 'i18n'
import { useNotify } from 'utils/useNotify'

const useStyles = makeStyles((theme) => ({
  ...commonStyles(theme),
  root: {
    display: 'flex',
    flexGrow: 1,
    padding: theme.spacing(0),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 3, 3, 3)
    },
    backgroundColor: theme.palette.background.light,
    minHeight: '100vh'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    height: 115,
    padding: theme.spacing(0, 1),
    overflow: 'hidden'
  },
  logo: {
    height: 44,
    marginRight: theme.spacing(2)
  },
  signOutLink: {
    fontWeight: theme.palette.inputs.label.semibold,
    color: theme.palette.inputs.label.color.regular
  },
  paper: {
    flexGrow: 1,
    borderRadius: theme.shape.borderRadius * 3,
    display: 'flex',

    padding: theme.spacing(6, 5)
  }
}))

const rootElement = () => {
  return document.getElementById('root')
}

const fetchSignUp = async (setSignUp: React.Dispatch<React.SetStateAction<SignUp>>) => {
  const signUp: SignUp = await fetchJson<SignUp>(`/${I18n.locale}/complete_sign_up/current`, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
  setSignUp(signUp)
}

const resolveStep = (step: SignUp['step']) => {
  switch (step) {
    case 'credentials':
      return [true, <CredentialsFormDescription key='desc' />, <CredentialsForm key='form' />] as const
    case 'confirmation':
      return [true, <ConfirmationFormDescription key='desc' />, <ConfirmationForm key='form' />] as const
    case 'company':
      return [true, <CompanyFormDescription key='desc' />, <CompanyForm key='form' />] as const
    case 'plan':
      return [false, <PlanForm key='form' />] as const
    case 'dataset':
    case 'provisioning_dataset':
    case 'provisioning_failed':
      return [false, <DatasetForm key='form' />] as const
    case 'completed':
      return [false, <CompletedForm key='form' />] as const
    default:
      throw `Unknown step: ${step}`
  }
}

const SignUpWizard: React.FC = () => {
  const { notifyError } = useNotify()
  useEffect(() => {
    const errors: [string, string][] = JSON.parse(rootElement()?.getAttribute('data-errors') ?? '[]')
    if (errors.length > 0) {
      errors.forEach(([_, message]) => notifyError({ message }))
    }
  }, [notifyError])

  const classes = useStyles()

  const [signUp, setSignUp] = useState<SignUp>({})

  if (signUp.step == null) {
    const element = rootElement()
    if (element?.getAttribute('data-fetch') === 'true' && Object.keys(signUp).length === 0) {
      fetchSignUp(setSignUp)
    } else {
      setSignUp({ step: 'credentials' })
    }
    return <Loading />
  }

  const [isWrappable, stepDescription, stepForm] = resolveStep(signUp.step)

  return (
    <SignupContext.Provider value={{ signUp, setSignUp }}>
      <div className={classes.root}>
        <Grid container direction='column' alignItems='stretch'>
          <Grid item container justifyContent='space-between' className={classes.header}>
            <Grid item>
              <img src={Logo} className={classes.logo} />
            </Grid>
            <Grid item>
              <Grid container alignItems='center' spacing={3}>
                {signUp.step && signUp.step != 'credentials' && (
                  <Grid item>
                    <a rel='nofollow' href={`/${I18n.locale}/sign_out`} className={classes.signOutLink}>
                      {t('sign_out')}
                    </a>
                  </Grid>
                )}
                <Grid item>
                  <LanguageSelect />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Container maxWidth='lg'>
              <Paper elevation={0} className={classes.paper}>
                {isWrappable ? (
                  <Grid container spacing={4}>
                    <Grid item xs={12} sm={6}>
                      {stepDescription}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper elevation={0} className={classes.formPaper}>
                        {stepForm}
                      </Paper>
                    </Grid>
                  </Grid>
                ) : (
                  stepDescription
                )}
              </Paper>
            </Container>
          </Grid>
        </Grid>
      </div>
    </SignupContext.Provider>
  )
}

export default SignUpWizard
